import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  Chip,
  Modal,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  IconButton,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  PersonRemove as PersonRemoveIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  getFullUsuarios,
} from "../services/ConnectDataBase";
import { manageUsers } from "../services/api/auth/ManageUsersApi";
import { getErrorMessage, notify } from "../services/ui/notify";
import { DEPARTAMENTS, SCREEN_IDS, typeConnect, BUTTON_PERMISSIONS } from "../constants/Constants";
import {
  ADMIN_CONTROLLER_OPTIONS_COLORS,
  BACKGROUND_COLORS,
  BORDER_COLORS,
  BRAND_COLORS,
  TEXT_COLORS,
} from "../style/colors.js";
import AppButton from "../components/Common/ui/AppButton";
import AppTextField from "../components/Common/ui/AppTextField";
import AppSelect from "../components/Common/ui/AppSelect";

const userQuery = "userQuery";

const AdminControllerOptions = () => {
  const currentScreenId = SCREEN_IDS.ADMIN_CONTROLLER_OPTIONS;
  const [usuarios, setUsuarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [correo, setCorreo] = useState("");
  const [nombre, setNombre] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [idioma, setIdioma] = useState("es");
  const [admin, setAdmin] = useState("No");
  const [genero, setGenero] = useState("Hombre");
  const [team, setTeam] = useState(Object.keys(DEPARTAMENTS)[0]);

  const [showModalDelete, setShowModalDelete] = useState(false);
  const [usuarioSelected, setUsuarioSelected] = useState("");
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [editingUser, setEditingUser] = useState({
    email: "",
    name: "",
    password: "",
    usuario_old: "",
    lang: "es",
    admin: "No",
    genero: "Hombre",
    alta: "",
    baja: "",
    team: Object.keys(DEPARTAMENTS)[0],
    permisos: [],
  });

  // Permisos para creación (en UI) => array numérico [1,2,3,...]
  const [createPermisos, setCreatePermisos] = useState([]);
  const [permSearchCreate, setPermSearchCreate] = useState("");
  const [permSearchEdit, setPermSearchEdit] = useState("");

  const permissionOptions = Object.entries(BUTTON_PERMISSIONS).map(
    ([key, value]) => ({
      id: Number(value),
      label: key.replace(/_/g, " ").toLowerCase(),
    })
  );

  const permissionLabelById = permissionOptions.reduce((acc, it) => {
    acc[it.id] = it.label;
    return acc;
  }, {});

  const normalizePermsToArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
    if (typeof value === "number") return [value];
    if (typeof value === "string") {
      return value
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((n) => !Number.isNaN(n));
    }
    return [];
  };

  const permsArrayToCommaString = (arr) =>
    (arr || []).map((n) => Number(n)).filter((n) => !Number.isNaN(n)).join(",");

  const normalizePermSearch = (value) => String(value || "").trim().toLowerCase();

  const getFilteredPermissions = (searchValue) => {
    const term = normalizePermSearch(searchValue);
    return permissionOptions.filter((opt) => {
      if (!term) return true;
      return opt.label.includes(term) || String(opt.id).includes(term);
    });
  };

  const getPermissionSummary = (selectedPerms) => {
    if (!selectedPerms?.length) return "Sin permisos seleccionados";
    const labels = selectedPerms
      .map((id) => permissionLabelById[id] ?? String(id))
      .filter(Boolean);
    const preview = labels.slice(0, 4).join(", ");
    return labels.length > 4 ? `${preview} +${labels.length - 4}` : preview;
  };

  const toggleCreatePerm = (permId) => {
    setCreatePermisos((prev) =>
      prev.includes(permId) ? prev.filter((id) => id !== permId) : [...prev, permId]
    );
  };

  const toggleEditPerm = (permId) => {
    setEditingUser((prev) => ({
      ...prev,
      permisos: prev.permisos.includes(permId)
        ? prev.permisos.filter((id) => id !== permId)
        : [...prev.permisos, permId],
    }));
  };

  const isUserDeleted = (usuario) => {
    const baja = usuario?.baja;
    if (!baja) return false;
    if (typeof baja === "object" && baja?.date) return Boolean(String(baja.date).trim());
    return Boolean(String(baja).trim());
  };

  const formatDisplayName = (value) => {
    if (!value) return "";
    return String(value)
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  useEffect(() => {
    operationLoad();
  }, []);

  const validateUserData = (user) => {
    if (!user.email || !user.name || !user.password) {
      notify.warn("Por favor completa todos los campos obligatorios.");
      return false;
    }
    return true;
  };

  const validateCreateData = () => {
    if (!correo || !nombre || !contrasena) {
      notify.warn("Por favor completa todos los campos obligatorios.");
      return false;
    }
    return true;
  };

  const validateEditData = (user) => {
    if (!user.email || !user.name) {
      notify.warn("Por favor completa los campos obligatorios.");
      return false;
    }
    return true;
  };

  const getCodTeam = (teamValue) => {
    // teamValue puede venir como label ("Engastado") o como id ("8")
    if (teamValue == null) return undefined;
    const s = String(teamValue).trim();
    const byLabel = Object.entries(DEPARTAMENTS).find(([, v]) => String(v) === s);
    if (byLabel) return Number(byLabel[0]);
    const byId = Object.keys(DEPARTAMENTS).find((k) => String(k) === s);
    if (byId) return Number(byId);
    return teamValue;
  };

  const handleEditUser = (usuario) => {
    const permisosArr = normalizePermsToArray(
      usuario?.nivel_acceso ?? usuario?.permisos
    );
    setEditingUser({
      email: usuario.usuario || "",
      usuario_old: usuario.usuario || "",
      name: usuario.name || "",
      password: usuario.password || "",
      lang: usuario.lang || "es",
      admin: usuario.admin === "S" ? "Si" : "No",
      genero: usuario.genero || "Hombre",
      alta: usuario.alta?.date?.split(" ")[0] || "",
      baja: usuario.baja?.date?.split(" ")[0] || "",
      team: usuario.team || Object.keys(DEPARTAMENTS)[0],
      permisos: permisosArr,
    });
    setPermSearchEdit("");
    setShowModalEdit(true);
  };

  const operationLoad = async () => {
    try {
      const listaOperarios = await getFullUsuarios(
        SCREEN_IDS.INFO_DIARIO,
        typeConnect[0],
        userQuery
      );
      if (!listaOperarios || listaOperarios.length === 0) {
        console.warn("No se encontraron usuarios.");
        setUsuarios([]);
        return;
      }
      setUsuarios(listaOperarios);
    } catch (error) {
      console.error("Error al cargar usuarios:", error);
    }
  };

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = usuarios.filter((usuario) => {
      const matchesSearch =
        usuario.name.toLowerCase().includes(term) ||
        usuario.team.toLowerCase().includes(term);
      return matchesSearch;
    });
    setFilteredUsuarios(filtered);
  }, [searchTerm, usuarios]);

  const handleCreateUser = async () => {
    try {
      if (!validateCreateData()) return;
      const result = await manageUsers({
        action: "create",
        usuario: correo,
        name: nombre,
        password: contrasena,
        lang: idioma,
        admin,
        genero: genero || undefined,
        cod_team: getCodTeam(team),
        permisos: permsArrayToCommaString(createPermisos),
        // minutos/minutosefectivosdia: si no los mandamos, aplica defaults del backend
      });

      if (result?.status === "ok") {
        console.log("Usuario creado correctamente:", result);
        setShowModal(false);
        setCreatePermisos([]);
        setPermSearchCreate("");
        operationLoad();
      } else {
        notify.error(result?.message || "Error al crear el usuario");
      }
    } catch (error) {
      console.error("Error al crear usuario:", error);
      notify.error(getErrorMessage(error, "Error al crear el usuario"));
    }
  };

  const handleDeleteUser = async () => {
    try {
      const result = await manageUsers({
        action: "delete",
        usuario: usuarioSelected,
      });
      if (result?.status === "ok") {
        console.log("Usuario eliminado correctamente:", result);
        setShowModalDelete(false);
        operationLoad();
      } else {
        notify.error(result?.message || "Error al eliminar el usuario");
      }
    } catch (error) {
      console.error("Error al eliminar usuario:", error);
      notify.error(getErrorMessage(error, "Error al eliminar el usuario"));
    }
  };

  const handleRestoreUser = async (usuario) => {
    try {
      const result = await manageUsers({
        action: "restore",
        usuario: usuario?.usuario,
      });
      if (result?.status === "ok") {
        console.log("Usuario restaurado correctamente:", result);
        operationLoad();
      } else {
        notify.error(result?.message || "Error al restaurar el usuario");
      }
    } catch (error) {
      console.error("Error al restaurar usuario:", error);
      notify.error(getErrorMessage(error, "Error al restaurar el usuario"));
    }
  };

  const handleSaveEditUser = async () => {
    if (!validateEditData(editingUser)) {
      return;
    }

    try {
      const result = await manageUsers({
        action: "update",
        usuario: editingUser.email,
        usuario_old: editingUser.usuario_old || editingUser.email,
        name: editingUser.name,
        lang: editingUser.lang,
        admin: editingUser.admin,
        genero: editingUser.genero || undefined,
        cod_team: getCodTeam(editingUser.team),
        permisos: permsArrayToCommaString(editingUser.permisos),
        ...(editingUser.password ? { password: editingUser.password } : {}),
        // minutos/minutosefectivosdia: si no los mandamos, usa defaults del backend (si aplica)
      });

      if (result?.status === "ok") {
        console.log("Usuario editado correctamente:", result);
        setShowModalEdit(false);
        setPermSearchEdit("");
        operationLoad();
      } else {
        notify.error(result?.message || "Error al editar el usuario");
      }
    } catch (error) {
      console.error("Error al editar usuario:", error);
      notify.error(getErrorMessage(error, "Error al editar el usuario"));
    }
  };

  const closeCreateModal = () => {
    setPermSearchCreate("");
    setShowModal(false);
  };

  const closeEditModal = () => {
    setPermSearchEdit("");
    setShowModalEdit(false);
  };

  const modalFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.8,
      backgroundColor: BACKGROUND_COLORS.CARD,
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.88rem",
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "0.92rem",
    },
  };

  const modalSectionSx = {
    border: `1px solid ${BORDER_COLORS.PRIMARY}`,
    borderRadius: 2.5,
    backgroundColor: BACKGROUND_COLORS.CARD,
    p: 2,
  };

  const renderPermissionPanel = ({ selected, onToggle, searchValue, onSearchChange }) => {
    const filteredPermissions = getFilteredPermissions(searchValue);

    return (
      <Box sx={modalSectionSx}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.92rem",
              fontWeight: 700,
              color: TEXT_COLORS.BRAND,
            }}
          >
            Permisos
          </Typography>
          <Chip
            size="small"
            label={`${selected.length} seleccionado${selected.length === 1 ? "" : "s"}`}
            sx={{
              backgroundColor: "rgba(23,64,69,0.08)",
              color: TEXT_COLORS.BRAND,
              fontWeight: 600,
            }}
          />
        </Box>

        <Typography
          sx={{
            mb: 1.25,
            fontSize: "0.78rem",
            color: TEXT_COLORS.SECONDARY,
          }}
        >
          {getPermissionSummary(selected)}
        </Typography>

        <AppTextField
          label="Buscar permiso"
          value={searchValue}
          onChange={onSearchChange}
          sx={{ ...modalFieldSx, mb: 1.25 }}
        />

        <Box
          sx={{
            border: `1px solid ${BORDER_COLORS.PRIMARY}`,
            borderRadius: 2,
            backgroundColor: BACKGROUND_COLORS.PRIMARY,
            p: 1,
            maxHeight: 240,
            overflowY: "auto",
          }}
        >
          {filteredPermissions.length === 0 ? (
            <Typography
              sx={{
                py: 2.5,
                textAlign: "center",
                fontSize: "0.82rem",
                color: TEXT_COLORS.SECONDARY,
              }}
            >
              No hay permisos que coincidan.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 0.75,
              }}
            >
              {filteredPermissions.map((opt) => {
                const checked = selected.includes(opt.id);
                return (
                  <Box
                    key={opt.id}
                    onClick={() => onToggle(opt.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.4,
                      px: 0.8,
                      py: 0.35,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      backgroundColor: checked ? "rgba(23,64,69,0.08)" : "transparent",
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        backgroundColor: checked
                          ? "rgba(23,64,69,0.12)"
                          : "rgba(15,23,42,0.04)",
                      },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={() => onToggle(opt.id)}
                      sx={{ p: "4px" }}
                    />
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        lineHeight: 1.2,
                        color: TEXT_COLORS.PRIMARY,
                      }}
                    >
                      {opt.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "85vh",
        p: 2,
        borderRadius: 3,
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
        border: "1px solid #e5e7eb",
        gap: 0,
      }}
    >
      {/* Contenido */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* Toolbar */}
        <Paper
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: "1px solid #e5e7eb",
            boxShadow: "none",
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "nowrap",
              overflowX: "auto",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Box sx={{ display: "flex", gap: 1.2, flex: "0 0 auto" }}>
              <AppButton
                variantType="secondary"
                startIcon={<PersonAddIcon />}
                onClick={() => {
                  setPermSearchCreate("");
                  setShowModal(true);
                }}
                sx={{ flex: "0 0 auto" }}
              >
                Añadir
              </AppButton>

              <AppButton
                variantType="secondary"
                startIcon={<PersonRemoveIcon />}
                onClick={() => setShowModalDelete(true)}
                sx={{ flex: "0 0 auto" }}
              >
                Eliminar
              </AppButton>
            </Box>

            <Box sx={{ flex: "0 0 auto" }}>
              <AppTextField
                label="Buscar usuario..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ width: 240 }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Tabla */}
        <Paper
          sx={{
            flex: 1,
            overflow: "hidden",
            borderRadius: 2,
            border: "1px solid #e5e7eb",
            boxShadow: "none",
          }}
        >
          <TableContainer sx={{ maxHeight: "100%", overflow: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Nombre",
                    "Equipo",
                    "Panel",
                    "Permisos",
                    "Acciones",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: ADMIN_CONTROLLER_OPTIONS_COLORS.BACKGROUND_TABLE_HEADER,
                        borderBottom: "none",
                        py: 0.7,
                        px: 1,
                        fontSize: "0.72rem",
                      }}
                      align={header === "Acciones" ? "center" : "left"}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredUsuarios.map((usuario) => (
                  (() => {
                    const deleted = isUserDeleted(usuario);
                    const permsArr = normalizePermsToArray(
                      usuario?.nivel_acceso ?? usuario?.permisos
                    );
                    return (
                  <TableRow
                    hover
                    key={usuario.usuario}
                    sx={{
                      "& td": { padding: "4px 8px" },
                      backgroundColor: deleted ? "rgba(107,114,128,0.08)" : "transparent",
                      opacity: deleted ? 0.75 : 1,
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          sx={{
                            textDecoration: deleted ? "line-through" : "none",
                            color: deleted ? "text.secondary" : "text.primary",
                            fontSize: "0.78rem",
                          }}
                        >
                          {formatDisplayName(usuario.name)}
                        </Typography>
                        {deleted ? <Chip size="small" label="Baja" color="default" /> : null}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        color: deleted ? "text.secondary" : "text.primary",
                        fontSize: "0.78rem",
                      }}
                    >
                      {usuario.team}
                    </TableCell>
                    <TableCell>
                      {usuario.admin === "S" ? (
                        <Chip size="small" label="Activo" color="primary" />
                      ) : (
                        <Chip size="small" label="--" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.72rem" }}>
                      {!permsArr.length
                        ? "—"
                        : (() => {
                            const first = permsArr
                              .slice(0, 3)
                              .map((id) => permissionLabelById[id] ?? id);
                            const rest = permsArr.length - first.length;
                            return rest > 0
                              ? `${first.join(", ")} +${rest}`
                              : first.join(", ");
                          })()}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                        {deleted ? (
                          <Button
                            variant="text"
                            color="success"
                            onClick={() => handleRestoreUser(usuario)}
                            sx={{ minWidth: 0, padding: 0 }}
                            title="Restaurar usuario"
                          >
                            <RestoreFromTrashIcon />
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="text"
                              color="primary"
                              onClick={() => handleEditUser(usuario)}
                              sx={{ minWidth: 0, padding: 0 }}
                            >
                              <EditIcon />
                            </Button>
                            <Button
                              variant="text"
                              color="error"
                              onClick={() => {
                                setUsuarioSelected(usuario.usuario);
                                setShowModalDelete(true);
                              }}
                              sx={{ minWidth: 0, padding: 0 }}
                            >
                              <PersonRemoveIcon />
                            </Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                    );
                  })()
                ))}

                {filteredUsuarios.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary" fontWeight={600}>
                        No se encontraron usuarios.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Dialog
        open={showModal}
        onClose={closeCreateModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: BACKGROUND_COLORS.PRIMARY,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: BRAND_COLORS.PRIMARY,
            color: TEXT_COLORS.LIGHT,
            px: 2.5,
            py: 1.8,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Crear Usuario
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", opacity: 0.9 }}>
              Configura los datos de acceso, el equipo y los permisos.
            </Typography>
          </Box>
          <IconButton onClick={closeCreateModal} sx={{ color: TEXT_COLORS.LIGHT }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "grid", gap: 1.5, pt: 1 }}>
            <Box sx={modalSectionSx}>
              <Typography sx={{ mb: 1.5, fontSize: "0.92rem", fontWeight: 700, color: TEXT_COLORS.BRAND }}>
                Datos básicos
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <AppTextField
                    label="Correo"
                    value={correo}
                    onChange={(e) => setCorreo(e.target.value)}
                    sx={modalFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <AppTextField
                    label="Nombre"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    sx={modalFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <AppTextField
                    label="Contraseña"
                    type="password"
                    value={contrasena}
                    onChange={(e) => setContrasena(e.target.value)}
                    sx={modalFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <AppTextField
                    select
                    label="Idioma"
                    value={idioma}
                    onChange={(e) => setIdioma(e.target.value)}
                    sx={modalFieldSx}
                  >
                    <MenuItem value="es">Español</MenuItem>
                    <MenuItem value="en">Inglés</MenuItem>
                  </AppTextField>
                </Grid>
              </Grid>
            </Box>

            <Box sx={modalSectionSx}>
              <Typography sx={{ mb: 1.5, fontSize: "0.92rem", fontWeight: 700, color: TEXT_COLORS.BRAND }}>
                Configuración
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <AppTextField
                    select
                    label="Panel"
                    value={admin}
                    onChange={(e) => setAdmin(e.target.value)}
                    sx={modalFieldSx}
                  >
                    <MenuItem value="Si">Sí</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </AppTextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <AppTextField
                    select
                    label="Género"
                    value={genero}
                    onChange={(e) => setGenero(e.target.value)}
                    sx={modalFieldSx}
                  >
                    <MenuItem value="Hombre">Hombre</MenuItem>
                    <MenuItem value="Mujer">Mujer</MenuItem>
                    <MenuItem value="NC">NC</MenuItem>
                  </AppTextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <AppTextField
                    select
                    label="Equipo"
                    value={team}
                    onChange={(e) => setTeam(e.target.value)}
                    sx={modalFieldSx}
                  >
                    {Object.keys(DEPARTAMENTS).map((key) => (
                      <MenuItem key={key} value={DEPARTAMENTS[key]}>
                        {DEPARTAMENTS[key]}
                      </MenuItem>
                    ))}
                  </AppTextField>
                </Grid>
              </Grid>
            </Box>

            {renderPermissionPanel({
              selected: createPermisos,
              onToggle: toggleCreatePerm,
              searchValue: permSearchCreate,
              onSearchChange: (e) => setPermSearchCreate(e.target.value),
            })}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            backgroundColor: BACKGROUND_COLORS.SECONDARY,
            borderTop: `1px solid ${BORDER_COLORS.PRIMARY}`,
            justifyContent: "space-between",
          }}
        >
          <AppButton onClick={handleCreateUser} sx={{ minWidth: 130 }}>
            Crear
          </AppButton>
          <AppButton
            variantType="secondary"
            onClick={closeCreateModal}
            sx={{ minWidth: 130 }}
          >
            Cancelar
          </AppButton>
        </DialogActions>
      </Dialog>

      <Modal open={showModalDelete} onClose={() => setShowModalDelete(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 1,
            width: 400,
          }}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Eliminar Usuario
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Seleccione un usuario</InputLabel>
            <AppSelect
              value={usuarioSelected}
              label='Seleccione un usuario'
              onChange={(e) => setUsuarioSelected(e.target.value)}
              fullWidth
            >
              {usuarios.map((usuario) => (
                <MenuItem key={usuario.usuario} value={usuario.usuario}>
                  {usuario.name}
                </MenuItem>
              ))}
            </AppSelect>
          </FormControl>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <AppButton onClick={handleDeleteUser}>
              Eliminar
            </AppButton>
            <AppButton
              variantType="secondary"
              onClick={() => setShowModalDelete(false)}
            >
              Cancelar
            </AppButton>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={showModalEdit}
        onClose={closeEditModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: BACKGROUND_COLORS.PRIMARY,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: BRAND_COLORS.PRIMARY,
            color: TEXT_COLORS.LIGHT,
            px: 2.5,
            py: 1.8,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Editar Usuario
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", opacity: 0.9 }}>
              Ajusta la configuración del usuario y revisa sus permisos.
            </Typography>
          </Box>
          <IconButton onClick={closeEditModal} sx={{ color: TEXT_COLORS.LIGHT }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "grid", gap: 1.5, pt: 1 }}>
            <Box sx={modalSectionSx}>
              <Typography sx={{ mb: 1.5, fontSize: "0.92rem", fontWeight: 700, color: TEXT_COLORS.BRAND }}>
                Datos básicos
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <AppTextField
                    label="Correo"
                    value={editingUser.email}
                    onChange={(e) =>
                      setEditingUser((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    sx={modalFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <AppTextField
                    label="Nombre"
                    value={editingUser.name}
                    onChange={(e) =>
                      setEditingUser((prev) => ({ ...prev, name: e.target.value }))
                    }
                    sx={modalFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <AppTextField
                    label="Contraseña"
                    type="password"
                    value={editingUser.password}
                    onChange={(e) =>
                      setEditingUser((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    sx={modalFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <AppTextField
                    select
                    label="Idioma"
                    value={editingUser.lang}
                    onChange={(e) =>
                      setEditingUser((prev) => ({
                        ...prev,
                        lang: e.target.value,
                      }))
                    }
                    sx={modalFieldSx}
                  >
                    <MenuItem value="es">Español</MenuItem>
                    <MenuItem value="en">Inglés</MenuItem>
                  </AppTextField>
                </Grid>
              </Grid>
            </Box>

            <Box sx={modalSectionSx}>
              <Typography sx={{ mb: 1.5, fontSize: "0.92rem", fontWeight: 700, color: TEXT_COLORS.BRAND }}>
                Configuración
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <AppTextField
                    select
                    label="Panel"
                    value={editingUser.admin}
                    onChange={(e) =>
                      setEditingUser((prev) => ({
                        ...prev,
                        admin: e.target.value,
                      }))
                    }
                    sx={modalFieldSx}
                  >
                    <MenuItem value="Si">Sí</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </AppTextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <AppTextField
                    select
                    label="Género"
                    value={editingUser.genero}
                    onChange={(e) =>
                      setEditingUser((prev) => ({
                        ...prev,
                        genero: e.target.value,
                      }))
                    }
                    sx={modalFieldSx}
                  >
                    <MenuItem value="Hombre">Hombre</MenuItem>
                    <MenuItem value="Mujer">Mujer</MenuItem>
                    <MenuItem value="NC">NC</MenuItem>
                  </AppTextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <AppTextField
                    select
                    label="Equipo"
                    value={editingUser.team}
                    onChange={(e) =>
                      setEditingUser((prev) => ({
                        ...prev,
                        team: e.target.value,
                      }))
                    }
                    sx={modalFieldSx}
                  >
                    {Object.keys(DEPARTAMENTS).map((key) => (
                      <MenuItem key={key} value={DEPARTAMENTS[key]}>
                        {DEPARTAMENTS[key]}
                      </MenuItem>
                    ))}
                  </AppTextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <AppTextField
                    label="Fecha de Alta"
                    type="date"
                    value={editingUser.alta}
                    onChange={(e) =>
                      setEditingUser((prev) => ({ ...prev, alta: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={modalFieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <AppTextField
                    label="Fecha de Baja"
                    type="date"
                    value={editingUser.baja}
                    onChange={(e) =>
                      setEditingUser((prev) => ({ ...prev, baja: e.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={modalFieldSx}
                  />
                </Grid>
              </Grid>
            </Box>

            {renderPermissionPanel({
              selected: editingUser.permisos || [],
              onToggle: toggleEditPerm,
              searchValue: permSearchEdit,
              onSearchChange: (e) => setPermSearchEdit(e.target.value),
            })}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            backgroundColor: BACKGROUND_COLORS.SECONDARY,
            borderTop: `1px solid ${BORDER_COLORS.PRIMARY}`,
            justifyContent: "space-between",
          }}
        >
          <AppButton onClick={handleSaveEditUser} sx={{ minWidth: 130 }}>
            Guardar
          </AppButton>
          <AppButton
            variantType="secondary"
            onClick={closeEditModal}
            sx={{ minWidth: 130 }}
          >
            Cancelar
          </AppButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminControllerOptions;
